import { c as _c } from "react/compiler-runtime";
import { Check } from 'lucide-react';
import { formatWeightValue } from '@/lib/format/format-weight';
import { useTranslation } from '@/hooks/use-translation';
import { demoDesigned, demoKeyNames, demoMeasured } from './demo-sample';

/** As many rows as the panel shows before the sheet fades out. */
const ROWS = 5;
/** The rows already ticked off at the bench. */
const DONE = 2;
const ROW_COLUMNS = 'grid-cols-[14px_34px_minmax(0,1fr)_minmax(0,1fr)]';

/**
 * The first rows of the demonstration piano's own adjustment sheet: what each
 * key reads now and what it should read once it is weighed off.
 *
 * A picture of a sheet rather than a table to read — the figures are the
 * demonstration piano's, and the page it is torn from carries eighty-eight of
 * them.
 */
export const AdjustmentSheetPreview = () => {
  const $ = _c(27);
  const {
    currentLanguage,
    t
  } = useTranslation();
  let t0;
  if ($[0] !== currentLanguage) {
    const weight = value => formatWeightValue(value, currentLanguage);
    t0 = (from, to) => `${weight(from)} → ${weight(to)}`;
    $[0] = currentLanguage;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const change = t0;
  let t1;
  if ($[2] !== t) {
    t1 = t("An extract of an adjustment sheet: front weight and spring tension, key by key, with a box to tick off each one.");
    $[2] = t;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  let t2;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <span />;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== t) {
    t3 = t("Note");
    $[5] = t;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== t3) {
    t4 = <span>{t3}</span>;
    $[7] = t3;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== t) {
    t5 = t("Front weight");
    $[9] = t;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  let t6;
  if ($[11] !== t5) {
    t6 = <span>{t5}</span>;
    $[11] = t5;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  let t7;
  if ($[13] !== t) {
    t7 = t("Spring");
    $[13] = t;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  let t8;
  if ($[15] !== t7) {
    t8 = <span>{t7}</span>;
    $[15] = t7;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  let t9;
  if ($[17] !== t4 || $[18] !== t6 || $[19] !== t8) {
    t9 = <div className={`grid ${ROW_COLUMNS} h-5 items-center gap-2 font-mono text-[9.5px] tracking-[0.06em] text-ptl-ink-3 uppercase`}>{t2}{t4}{t6}{t8}</div>;
    $[17] = t4;
    $[18] = t6;
    $[19] = t8;
    $[20] = t9;
  } else {
    t9 = $[20];
  }
  let t10;
  if ($[21] !== change) {
    t10 = Array.from({
      length: ROWS
    }, (_, index) => {
      const isDone = index < DONE;
      return <div key={demoKeyNames[index]} className={`grid ${ROW_COLUMNS} h-[26px] items-center gap-2 border-t border-border font-mono text-[11px] ${isDone ? "text-ptl-ink-3 line-through decoration-input" : ""}`}><span className={isDone ? "flex size-[11px] items-center justify-center rounded-[2px] bg-ptl-brass text-card" : "block size-[11px] rounded-[2px] border border-input"}>{isDone ? <Check className="size-2" strokeWidth={3} /> : null}</span><span>{demoKeyNames[index]}</span><span className="truncate">{change(demoMeasured.frontWeight[index] ?? null, demoDesigned.frontWeight[index] ?? null)}</span><span className="truncate">{change(demoMeasured.springBalance[index] ?? null, demoDesigned.springBalance[index] ?? null)}</span></div>;
    });
    $[21] = change;
    $[22] = t10;
  } else {
    t10 = $[22];
  }
  let t11;
  if ($[23] !== t1 || $[24] !== t10 || $[25] !== t9) {
    t11 = <div role="img" aria-label={t1} className="min-h-0 flex-1 overflow-hidden rounded-[3px] border border-border bg-card px-2.5 pt-2 [-webkit-mask-image:linear-gradient(#000_62%,transparent)] [mask-image:linear-gradient(#000_62%,transparent)]">{t9}{t10}</div>;
    $[23] = t1;
    $[24] = t10;
    $[25] = t9;
    $[26] = t11;
  } else {
    t11 = $[26];
  }
  return t11;
};